import {
  MAX_REQUEST_ITEM_QUANTITY,
  MAX_REQUEST_NOTE_LENGTH,
  type CreateServiceRequestItemInput,
} from '@hotel-voice/shared';
import { multiplyMoney, sumMoney } from '@/lib/money';
import type { HousekeepingService } from '@/types';

/**
 * What the guest has ticked on the housekeeping page: service id → quantity.
 * Ticking adds at quantity 1; the stepper changes it; unticking removes it.
 */
export type HousekeepingSelection = Record<string, number>;

export function toggleService(
  selection: HousekeepingSelection,
  serviceId: string,
): HousekeepingSelection {
  if (serviceId in selection) {
    const { [serviceId]: _removed, ...rest } = selection;
    return rest;
  }
  return { ...selection, [serviceId]: 1 };
}

export function setServiceQuantity(
  selection: HousekeepingSelection,
  serviceId: string,
  quantity: number,
): HousekeepingSelection {
  const clamped = Math.min(MAX_REQUEST_ITEM_QUANTITY, Math.floor(quantity));
  if (clamped <= 0) return toggleService(selection, serviceId);
  return { ...selection, [serviceId]: clamped };
}

export interface SelectedLine {
  service: HousekeepingService;
  quantity: number;
  unitCharge: number;
  lineTotal: number;
}

/** Ticked services joined to the catalogue, with the charge each will carry. */
export function selectedLines(
  selection: HousekeepingSelection,
  services: readonly HousekeepingService[],
): SelectedLine[] {
  return services.flatMap((service) => {
    const quantity = selection[service.id];
    if (!quantity) return [];
    const unitCharge = service.type === 'paid' ? service.charge : 0;
    return [{ service, quantity, unitCharge, lineTotal: multiplyMoney(unitCharge, quantity) }];
  });
}

export function selectionTotal(lines: readonly SelectedLine[]): number {
  return sumMoney(lines.map((line) => line.lineTotal));
}

export function toRequestItems(lines: readonly SelectedLine[]): CreateServiceRequestItemInput[] {
  return lines.map((line) => ({ id: line.service.id, quantity: line.quantity }));
}

/**
 * Whether there is anything to send. A housekeeping request may be lines, a
 * note alone ("Can't find what you need?"), or both — but not nothing.
 */
export function canSubmitHousekeeping(lines: readonly SelectedLine[], note: string): boolean {
  const trimmed = note.trim();
  return (lines.length > 0 || trimmed.length > 0) && trimmed.length <= MAX_REQUEST_NOTE_LENGTH;
}
